<template>
  <div class="admin-manage-page">
    <header class="page-header">
      <div>
        <p class="eyebrow">DERAS REVIEW CENTER</p>
        <h1>DeRAS复核与仲裁中心</h1>
        <p>集中处理机器人履约异常、SDK/GPS可信证据、用户拒绝确认和人工仲裁，让任务履约形成可追溯闭环。</p>
      </div>
      <button class="primary-btn" @click="fetchReviews">刷新复核</button>
    </header>

    <section class="metric-grid">
      <article class="metric-card accent">
        <span>待处理复核</span>
        <strong>{{ summary.pendingReviews || 0 }}</strong>
        <p>需要管理员处理</p>
      </article>
      <article class="metric-card danger">
        <span>高风险异常</span>
        <strong>{{ summary.dangerReviews || 0 }}</strong>
        <p>红色预警提交</p>
      </article>
      <article class="metric-card">
        <span>异常复核</span>
        <strong>{{ summary.alertReviews || 0 }}</strong>
        <p>SDK / GPS / 停滞</p>
      </article>
      <article class="metric-card">
        <span>人工仲裁</span>
        <strong>{{ summary.arbitrationReviews || 0 }}</strong>
        <p>拒绝开始/结束</p>
      </article>
      <article class="metric-card">
        <span>已处理</span>
        <strong>{{ summary.resolvedReviews || 0 }}</strong>
        <p>闭环完成</p>
      </article>
    </section>

    <section class="filter-card">
      <el-input v-model.trim="filters.keyword" clearable placeholder="搜索复核ID / 订单 / 任务 / 机器人 / 用户" @keyup.enter="fetchReviews"></el-input>
      <el-select v-model="filters.reviewStatus" clearable placeholder="处理状态" @change="fetchReviews">
        <el-option label="待处理" value="PENDING"></el-option>
        <el-option label="处理中" value="PROCESSING"></el-option>
        <el-option label="已处理" value="RESOLVED"></el-option>
        <el-option label="已驳回" value="REJECTED"></el-option>
      </el-select>
      <el-select v-model="filters.eventType" clearable placeholder="复核类型" @change="fetchReviews">
        <el-option label="异常复核" value="ALERT_REVIEW"></el-option>
        <el-option label="人工仲裁" value="ARBITRATION"></el-option>
      </el-select>
      <el-select v-model="filters.eventLevel" clearable placeholder="风险等级" @change="fetchReviews">
        <el-option label="高风险" value="DANGER"></el-option>
        <el-option label="黄色预警" value="WARNING"></el-option>
        <el-option label="信息记录" value="INFO"></el-option>
      </el-select>
      <button class="primary-btn" @click="fetchReviews">查询</button>
    </section>

    <section class="table-card">
      <el-table v-loading="loading" :data="reviews" @row-click="openDetail">
        <el-table-column prop="id" label="复核ID" width="90"></el-table-column>
        <el-table-column label="复核事件" min-width="220">
          <template #default="{ row }">
            <strong>{{ row.eventTitle || '异常复核' }}</strong>
            <small>{{ eventTypeLabel(row.eventType) }} · {{ proofTypeLabel(row.proofType) }}</small>
          </template>
        </el-table-column>
        <el-table-column label="任务/订单" min-width="210">
          <template #default="{ row }">
            <span>{{ row.taskTitle || '未命名任务' }}</span>
            <small>订单 #{{ row.orderId || '-' }} · {{ serviceTypeLabel(row.serviceType) }}</small>
          </template>
        </el-table-column>
        <el-table-column label="机器人" min-width="160">
          <template #default="{ row }">
            <span>{{ row.robotName || row.robotId || '-' }}</span>
            <small>{{ row.robotCategory || '-' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="双方用户" min-width="170">
          <template #default="{ row }">
            <span>发布：{{ row.publisherName || '-' }}</span>
            <small>服务：{{ row.takerName || '-' }}</small>
          </template>
        </el-table-column>
        <el-table-column label="等级" width="110">
          <template #default="{ row }">
            <el-tag :type="levelTag(row.eventLevel)">{{ levelLabel(row.eventLevel) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="状态" width="130">
          <template #default="{ row }">
            <el-tag :type="reviewTag(row.reviewStatus)">{{ reviewStatusLabel(row.reviewStatus) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="提交时间" min-width="170"></el-table-column>
        <el-table-column label="操作" width="360">
          <template #default="{ row }">
            <el-button size="mini" type="success" :disabled="isReviewClosed(row)" @click.stop="handleReview(row, 'RESOLVE')">标记处理</el-button>
            <el-button size="mini" :disabled="isReviewClosed(row)" @click.stop="handleReview(row, 'ARBITRATION')">转仲裁</el-button>
            <el-button size="mini" type="warning" :disabled="isReviewClosed(row)" @click.stop="handleReview(row, 'SUPPORT_PUBLISHER')">支持发布者</el-button>
            <el-button size="mini" type="primary" :disabled="isReviewClosed(row)" @click.stop="handleReview(row, 'SUPPORT_TAKER')">支持服务者</el-button>
            <el-button size="mini" type="danger" :disabled="isReviewClosed(row)" @click.stop="handleReview(row, 'REJECT')">驳回</el-button>
          </template>
        </el-table-column>
      </el-table>
    </section>

    <el-drawer
      v-model="detailVisible"
      size="58%"
      custom-class="review-drawer"
      append-to-body
      :with-header="false"
    >
      <div v-if="current" class="review-detail">
        <header class="drawer-head">
          <div>
            <p class="eyebrow">REVIEW DETAIL</p>
            <h2>{{ current.eventTitle || 'DeRAS复核详情' }}</h2>
            <p>{{ current.eventContent || '暂无复核说明' }}</p>
          </div>
          <button class="secondary-btn" @click="detailVisible = false">关闭</button>
        </header>

        <section class="detail-grid">
          <div><span>复核状态</span><strong>{{ reviewStatusLabel(current.reviewStatus) }}</strong></div>
          <div><span>事件等级</span><strong>{{ levelLabel(current.eventLevel) }}</strong></div>
          <div><span>证明类型</span><strong>{{ proofTypeLabel(current.proofType) }}</strong></div>
          <div><span>证明状态</span><strong>{{ proofStatusLabel(current.proofStatus) }}</strong></div>
          <div><span>复核来源</span><strong>{{ operatorRoleLabel(current.operatorRole) }}</strong></div>
          <div><span>提交时间</span><strong>{{ current.createTime || '-' }}</strong></div>
          <div><span>任务</span><strong>{{ current.taskTitle || current.taskId || '-' }}</strong></div>
          <div><span>机器人</span><strong>{{ current.robotName || current.robotId || '-' }}</strong></div>
          <div><span>订单状态</span><strong>{{ orderStatusLabel(current.orderStatus) }}</strong></div>
          <div><span>流程阶段</span><strong>{{ workflowLabel(current.workflowStatus || current.eventStage) }}</strong></div>
          <div><span>GPS坐标</span><strong>{{ coordinateText(current.robotLng, current.robotLat) }}</strong></div>
          <div><span>SDK版本</span><strong>{{ current.sdkVersion || '-' }}</strong></div>
        </section>

        <section class="decision-panel" :class="current.eventLevel">
          <span>处理建议</span>
          <strong>{{ reviewSuggestion(current) }}</strong>
          <p>处理结果会写入履约日志，并同步通知任务发布者和服务者，后续可作为 DeRAS 仲裁和结算依据。</p>
        </section>

        <section class="closure-panel" :class="{ closed: isReviewClosed(current), pending: !isReviewClosed(current) }">
          <div>
            <span>闭环状态</span>
            <strong>{{ reviewClosureTitle(current) }}</strong>
            <p>{{ reviewClosureDesc(current) }}</p>
          </div>
          <div class="closure-grid">
            <span><em>处理后订单</em><b>{{ orderStatusLabel(current.orderStatus) }}</b></span>
            <span><em>机器人状态</em><b>{{ robotReleaseLabel(current.robotReleaseStatus) }}</b></span>
            <span><em>待复核证据</em><b>{{ current.pendingReviewCount || 0 }} 条</b></span>
            <span><em>履约日志</em><b>{{ current.fulfillmentLogCount || detailLogs.length || 0 }} 条</b></span>
          </div>
        </section>

        <section class="review-actions">
          <button class="primary-btn" :disabled="isReviewClosed(current)" @click="handleReview(current, 'RESOLVE')">标记已处理</button>
          <button class="secondary-btn" :disabled="isReviewClosed(current)" @click="handleReview(current, 'ARBITRATION')">转人工仲裁</button>
          <button class="warning-btn" :disabled="isReviewClosed(current)" @click="handleReview(current, 'SUPPORT_PUBLISHER')">支持发布者并取消</button>
          <button class="primary-btn" :disabled="isReviewClosed(current)" @click="handleReview(current, 'SUPPORT_TAKER')">支持服务者并结算</button>
          <button class="secondary-btn" :disabled="isReviewClosed(current)" @click="handleReview(current, 'FORCE_FINISH')">强制完成订单</button>
          <button class="danger-btn" :disabled="isReviewClosed(current)" @click="handleReview(current, 'CANCEL_ORDER')">取消订单</button>
          <button class="danger-btn" :disabled="isReviewClosed(current)" @click="handleReview(current, 'REJECT')">驳回复核</button>
        </section>

        <section class="detail-section" v-if="current.order">
          <div class="section-title">
            <p class="eyebrow">ORDER SNAPSHOT</p>
            <h3>订单与结算快照</h3>
          </div>
          <div class="settlement-grid">
            <span><em>平台补助</em><b>{{ formatCoin(current.order.prepaidRobocoin) }} ROC</b></span>
            <span><em>最终应付</em><b>{{ formatCoin(current.order.finalRobocoin) }} ROC</b></span>
            <span><em>平台手续费</em><b>{{ formatCoin(current.order.platformFeeRobocoin) }} ROC</b></span>
            <span><em>服务方收入</em><b>{{ formatCoin(current.order.providerIncomeRobocoin) }} ROC</b></span>
          </div>
          <p class="detail-note">{{ current.order.settlementNote || current.order.derasNote || '暂无结算说明' }}</p>
        </section>

        <section class="detail-section">
          <div class="section-title">
            <p class="eyebrow">DECISION & PROOF</p>
            <h3>裁决结果与可信证据</h3>
          </div>
          <div class="proof-grid">
            <article>
              <span>最新裁决</span>
              <strong>{{ current.latestDecisionTitle || (isReviewClosed(current) ? reviewStatusLabel(current.reviewStatus) : '等待管理员处理') }}</strong>
              <p>{{ current.latestDecisionNote || current.reviewNote || '暂无裁决说明。' }}</p>
              <em v-if="current.latestDecisionTime">处理时间：{{ current.latestDecisionTime }}</em>
            </article>
            <article>
              <span>证据摘要</span>
              <strong>{{ proofTypeLabel(current.latestProofType || current.proofType) }} · {{ proofStatusLabel(current.latestProofStatus || current.proofStatus) }}</strong>
              <p class="digest-text">{{ current.latestProofDigest || current.proofDigest || '暂无证据摘要' }}</p>
            </article>
            <article v-if="current.arbitrationReason || current.arbitrationImageUrl">
              <span>用户仲裁材料</span>
              <strong>{{ workflowLabel(current.arbitrationStage || current.eventStage) }}</strong>
              <p>{{ current.arbitrationReason || '用户未填写更多说明。' }}</p>
              <a v-if="current.arbitrationImageUrl" :href="current.arbitrationImageUrl" target="_blank" rel="noopener">查看凭证图片</a>
            </article>
          </div>
        </section>

        <section class="detail-section">
          <div class="section-title">
            <p class="eyebrow">FULFILLMENT LOG</p>
            <h3>履约证据日志</h3>
          </div>
          <div class="log-list">
            <div v-for="log in detailLogs" :key="log.id" class="log-item" :class="log.eventLevel">
              <i></i>
              <div>
                <span>{{ log.createTime || '-' }} · {{ eventTypeLabel(log.eventType) }}</span>
                <strong>{{ log.eventTitle || '履约记录' }}</strong>
                <p>{{ log.eventContent || log.proofDigest || '-' }}</p>
              </div>
            </div>
            <p v-if="!detailLogs.length" class="empty-note">暂无履约日志。</p>
          </div>
        </section>

        <section class="detail-section">
          <div class="section-title">
            <p class="eyebrow">SDK VERIFY</p>
            <h3>阶段验签记录</h3>
          </div>
          <el-table :data="current.stageVerifications || []">
            <el-table-column prop="id" label="ID" width="80"></el-table-column>
            <el-table-column label="阶段" width="120">
              <template #default="{ row }">{{ workflowLabel(row.stage) }}</template>
            </el-table-column>
            <el-table-column label="状态" width="110">
              <template #default="{ row }"><el-tag :type="verificationTag(row.status)">{{ row.status || '-' }}</el-tag></template>
            </el-table-column>
            <el-table-column prop="publicKeyFingerprint" label="TPM指纹" min-width="210"></el-table-column>
            <el-table-column prop="verificationMessage" label="说明" min-width="200"></el-table-column>
          </el-table>
        </section>
      </div>
    </el-drawer>
  </div>
</template>

<script>
import {
  getAdminDerasReviewDetail,
  getAdminDerasReviews,
  handleAdminDerasReview
} from '../api/market'

export default {
  name: 'AdminDerasReviews',
  data() {
    return {
      loading: false,
      reviews: [],
      summary: {},
      detailVisible: false,
      current: null,
      filters: {
        keyword: '',
        reviewStatus: '',
        eventType: '',
        eventLevel: ''
      }
    }
  },
  computed: {
    detailLogs() {
      return (this.current && this.current.fulfillmentLogs) || []
    }
  },
  created() {
    this.fetchReviews()
  },
  methods: {
    async fetchReviews() {
      this.loading = true
      try {
        const response = await getAdminDerasReviews({
          keyword: this.filters.keyword || undefined,
          reviewStatus: this.filters.reviewStatus || undefined,
          eventType: this.filters.eventType || undefined,
          eventLevel: this.filters.eventLevel || undefined
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '复核列表加载失败')
          return
        }
        const data = response.data.data || {}
        this.reviews = data.records || []
        this.summary = data.summary || {}
      } catch (error) {
        this.$message.error('复核列表请求失败')
      } finally {
        this.loading = false
      }
    },
    async openDetail(row) {
      try {
        const response = await getAdminDerasReviewDetail(row.id)
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '复核详情加载失败')
          return
        }
        this.current = response.data.data
        this.detailVisible = true
      } catch (error) {
        this.$message.error('复核详情请求失败')
      }
    },
    isReviewClosed(row) {
      return row && ['RESOLVED', 'REJECTED'].includes(row.reviewStatus)
    },
    async handleReview(row, action) {
      if (this.isReviewClosed(row)) {
        this.$message.info('该复核记录已经闭环，无需重复处理')
        return
      }
      const titleMap = {
        REJECT: '驳回复核',
        ARBITRATION: '转人工仲裁',
        SUPPORT_PUBLISHER: '仲裁裁决：支持发布者',
        SUPPORT_TAKER: '仲裁裁决：支持服务者',
        CANCEL_ORDER: '仲裁裁决：取消订单',
        FORCE_FINISH: '仲裁裁决：强制完成'
      }
      const defaultReasonMap = {
        REJECT: '经复核，暂未发现需要平台介入的问题。',
        ARBITRATION: '管理员已将该异常转入人工仲裁，请双方等待平台裁决。',
        SUPPORT_PUBLISHER: '经人工仲裁，支持发布者申诉，订单取消并释放机器人。',
        SUPPORT_TAKER: '经人工仲裁，支持服务者履约结果，订单按平台补助结算。',
        CANCEL_ORDER: '经人工仲裁，订单取消，释放机器人并保留履约证据。',
        FORCE_FINISH: '经人工仲裁，管理员强制完成订单并生成结算记录。'
      }
      const title = titleMap[action] || '标记已处理'
      try {
        const result = await this.$prompt('请输入处理说明，处理结果会写入履约日志并通知双方用户。', title, {
          confirmButtonText: '确认处理',
          cancelButtonText: '取消',
          inputValue: defaultReasonMap[action] || '管理员已完成DeRAS复核处理。',
          inputValidator: value => Boolean(value && value.trim()),
          inputErrorMessage: '请填写处理说明'
        })
        const response = await handleAdminDerasReview(row.id, {
          action,
          reason: result.value
        })
        if (response.data.code !== 200) {
          this.$message.error(response.data.msg || '处理失败')
          return
        }
        this.$message.success('复核处理已写入履约日志')
        await this.fetchReviews()
        if (this.detailVisible) {
          await this.openDetail({ id: row.id })
        }
      } catch (error) {
        if (error !== 'cancel' && error !== 'close') {
          this.$message.error('处理失败')
        }
      }
    },
    formatCoin(value) {
      const numberValue = Number(value || 0)
      return Number.isFinite(numberValue) ? numberValue.toFixed(2) : '0.00'
    },
    coordinateText(lng, lat) {
      const parsedLng = Number(lng)
      const parsedLat = Number(lat)
      if (!Number.isFinite(parsedLng) || !Number.isFinite(parsedLat)) return '暂无坐标'
      return `${parsedLng.toFixed(6)}, ${parsedLat.toFixed(6)}`
    },
    eventTypeLabel(value) {
      if (value === 'ALERT_REVIEW') return '异常复核'
      if (value === 'ARBITRATION') return '人工仲裁'
      if (value === 'REVIEW_RESULT') return '处理结果'
      if (value === 'WORKFLOW') return '流程记录'
      if (value === 'SDK_VERIFY') return 'SDK验签'
      if (value === 'SDK_HEARTBEAT') return 'SDK心跳'
      if (value === 'SETTLEMENT') return '结算凭证'
      return value || '履约记录'
    },
    proofTypeLabel(value) {
      const map = {
        TPM_SIGNATURE: 'TPM签名',
        GPS: 'GPS定位',
        USER_CONFIRM: '用户确认',
        DERAS_AUDIT: 'DeRAS审核',
        SETTLEMENT: '结算凭证'
      }
      return map[value] || value || '未记录'
    },
    proofStatusLabel(value) {
      const map = {
        PENDING: '待处理',
        VERIFIED: '已验证',
        FAILED: '失败',
        REVIEWING: '复核中',
        RESOLVED: '已处理',
        REJECTED: '已驳回'
      }
      return map[value] || value || '未记录'
    },
    operatorRoleLabel(value) {
      const map = {
        PUBLISHER: '任务发布者',
        TAKER: '服务者',
        ADMIN: '管理员',
        DERAS: 'DeRAS系统',
        SDK: 'SDK Agent',
        SYSTEM: '系统'
      }
      return map[value] || value || '未记录'
    },
    reviewSuggestion(row) {
      if (!row) return '请查看履约日志后处理。'
      if (row.eventType === 'ARBITRATION') {
        return '优先核对用户拒绝理由、现场图片、履约日志和SDK验签记录，再决定是否支持发布者或服务者。'
      }
      if (row.proofType === 'TPM_SIGNATURE') {
        return '优先核对SDK Agent在线状态、TPM公钥指纹和阶段验签记录，确认是否为绑定机器人执行。'
      }
      if (row.proofType === 'GPS') {
        return '优先核对GPS更新时间、路线偏离和车辆停滞情况，必要时转入人工仲裁。'
      }
      if (row.eventLevel === 'DANGER') {
        return '该记录为高风险异常，建议转入人工仲裁并保留结算冻结。'
      }
      return '可结合履约日志、消息记录和订单阶段判断，确认后标记处理或驳回。'
    },
    reviewStatusLabel(value) {
      const map = {
        PENDING: '待处理',
        PROCESSING: '处理中',
        RESOLVED: '已处理',
        REJECTED: '已驳回'
      }
      return map[value] || value || '待处理'
    },
    reviewTag(value) {
      if (value === 'RESOLVED') return 'success'
      if (value === 'REJECTED') return 'info'
      if (value === 'PROCESSING') return 'warning'
      return 'danger'
    },
    levelLabel(value) {
      if (value === 'DANGER') return '红色'
      if (value === 'WARNING') return '黄色'
      return '信息'
    },
    levelTag(value) {
      if (value === 'DANGER') return 'danger'
      if (value === 'WARNING') return 'warning'
      return 'info'
    },
    serviceTypeLabel(value) {
      if (value === 'SHUTTLE') return '接驳服务'
      if (value === 'HOME_SERVICE') return '上门服务'
      return value || '未知服务'
    },
        orderStatusLabel(value) {
      const map = {
        ACCEPTED: '已接单',
        RUNNING: '进行中',
        FINISHED: '已完成',
        CANCELLED: '已取消',
        ARBITRATION: '仲裁中'
      }
      return map[value] || value || '未知'
    },
    robotReleaseLabel(value) {
      if (value === 'RELEASED') return '已释放/空闲'
      if (value === 'LOCKED') return '仍锁定任务'
      return '未同步'
    },
    reviewClosureTitle(row) {
      if (!row) return '等待同步'
      if (!this.isReviewClosed(row)) return '等待管理员处理'
      if (row.latestDecisionTitle) return row.latestDecisionTitle
      return row.reviewStatus === 'REJECTED' ? '复核已驳回' : '复核已闭环'
    },
    reviewClosureDesc(row) {
      if (!row) return 'DeRAS 正在同步复核状态。'
      if (!this.isReviewClosed(row)) return '该记录还未完成处理，订单、结算和机器人释放状态仍可能继续变化。'
      if (row.latestDecisionNote) return row.latestDecisionNote
      if (row.reviewNote) return row.reviewNote
      return '处理结果已写入履约日志，并通知任务发布者和服务者。'
    },
    workflowLabel(value) {
      const map = {
        ACCEPTED: '机器人已接单',
        DEPARTED: '机器人已出发',
        ARRIVED: '机器人已到达',
        START_REQUESTED: '申请开始',
        STARTED: '用户确认开始',
        END_REQUESTED: '申请结束',
        FINISHED: '用户确认结束'
      }
      return map[value] || value || '-'
    },
    verificationTag(value) {
      if (value === 'PASSED' || value === 'USED') return 'success'
      if (value === 'FAILED') return 'danger'
      if (value === 'EXPIRED') return 'info'
      return 'warning'
    }
  }
}
</script>

<style scoped>
.admin-manage-page {
  min-height: 100vh;
  padding: 28px;
  color: #f6fbff;
}

.page-header,
.metric-card,
.filter-card,
.table-card,
.detail-section {
  border: 1px solid rgba(143, 241, 199, 0.14);
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.06);
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  padding: 24px;
  margin-bottom: 18px;
}

.eyebrow {
  margin: 0 0 8px;
  color: #8ff1c7;
  font-size: 12px;
  letter-spacing: 0.18em;
}

.page-header h1,
.drawer-head h2,
.section-title h3 {
  margin: 0;
}

.page-header p,
.metric-card p,
small,
.drawer-head p,
.detail-note {
  color: rgba(246, 251, 255, 0.58);
}

.metric-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.metric-card {
  padding: 18px;
}

.metric-card.accent {
  border-color: rgba(255, 208, 138, 0.26);
  background: linear-gradient(135deg, rgba(255, 208, 138, 0.12), rgba(143, 241, 199, 0.08));
}

.metric-card.danger {
  border-color: rgba(255, 130, 130, 0.24);
  background: rgba(255, 130, 130, 0.08);
}

.metric-card span {
  color: rgba(143, 241, 199, 0.84);
}

.metric-card strong {
  display: block;
  margin-top: 10px;
  font-size: 30px;
}

.filter-card {
  display: grid;
  grid-template-columns: minmax(260px, 1fr) 150px 150px 150px 110px;
  gap: 12px;
  padding: 16px;
  margin-bottom: 18px;
}

.table-card {
  padding: 16px;
}

.primary-btn,
.secondary-btn,
.warning-btn,
.danger-btn {
  height: 40px;
  min-width: 108px;
  border-radius: 999px;
  font-weight: 800;
  cursor: pointer;
}

.primary-btn {
  border: none;
  color: #03101d;
  background: linear-gradient(135deg, #8ff1c7, #ffd08a);
}

.secondary-btn {
  border: 1px solid rgba(143, 241, 199, 0.22);
  color: #dff8ff;
  background: rgba(143, 241, 199, 0.08);
}

.warning-btn {
  border: 1px solid rgba(255, 208, 138, 0.72);
  color: #241300;
  background: linear-gradient(135deg, #ffd08a, #ffb84f);
}

.danger-btn {
  border: 1px solid rgba(255, 105, 105, 0.42);
  color: #fff;
  background: rgba(240, 68, 97, 0.75);
}

.review-detail {
  min-height: 100vh;
  padding: 24px;
  color: #f6fbff;
  background:
    radial-gradient(circle at 10% 0%, rgba(143, 241, 199, 0.14), transparent 30%),
    linear-gradient(180deg, #06111e, #050b15);
}

.drawer-head {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 18px;
}

.detail-grid,
.settlement-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.detail-grid div,
.settlement-grid span {
  padding: 14px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.detail-grid span,
.settlement-grid em {
  display: block;
  margin-bottom: 7px;
  color: rgba(143, 241, 199, 0.84);
  font-size: 12px;
  font-style: normal;
}

.detail-grid strong,
.settlement-grid b {
  color: #fff;
  word-break: break-word;
}

.decision-panel {
  padding: 16px;
  margin-bottom: 18px;
  border-radius: 18px;
  border: 1px solid rgba(255, 208, 138, 0.24);
  background: rgba(255, 208, 138, 0.08);
}

.decision-panel.DANGER {
  border-color: rgba(255, 105, 105, 0.32);
  background: rgba(240, 68, 97, 0.1);
}

.decision-panel span {
  display: block;
  margin-bottom: 8px;
  color: #ffd08a;
  font-size: 12px;
  font-weight: 900;
}

.decision-panel strong {
  display: block;
  color: #fff;
  line-height: 1.55;
}

.decision-panel p,
.empty-note {
  margin: 8px 0 0;
  color: rgba(246, 251, 255, 0.62);
  line-height: 1.65;
}

.closure-panel {
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.25fr);
  gap: 14px;
  padding: 16px;
  margin-bottom: 18px;
  border-radius: 18px;
  border: 1px solid rgba(143, 241, 199, 0.18);
  background: rgba(143, 241, 199, 0.08);
}

.closure-panel.pending {
  border-color: rgba(255, 208, 138, 0.28);
  background: rgba(255, 208, 138, 0.08);
}

.closure-panel.closed {
  border-color: rgba(143, 241, 199, 0.28);
  background: linear-gradient(135deg, rgba(143, 241, 199, 0.12), rgba(90, 184, 255, 0.07));
}

.closure-panel span,
.proof-grid span {
  display: block;
  margin-bottom: 8px;
  color: rgba(143, 241, 199, 0.84);
  font-size: 12px;
  font-weight: 900;
}

.closure-panel strong,
.proof-grid strong {
  display: block;
  color: #fff;
  line-height: 1.5;
}

.closure-panel p,
.proof-grid p,
.proof-grid em {
  margin: 8px 0 0;
  color: rgba(246, 251, 255, 0.62);
  line-height: 1.65;
  font-style: normal;
}

.closure-grid,
.proof-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.closure-grid span,
.proof-grid article {
  padding: 13px;
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.055);
}

.closure-grid em {
  display: block;
  margin-bottom: 7px;
  color: rgba(246, 251, 255, 0.54);
  font-size: 12px;
  font-style: normal;
}

.closure-grid b {
  color: #fff;
}

.proof-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.proof-grid a {
  display: inline-flex;
  margin-top: 10px;
  color: #8ff1c7;
  font-weight: 800;
  text-decoration: none;
}

.digest-text {
  word-break: break-all;
  font-family: ui-monospace, monospace;
}

.review-actions {
  display: flex;
  gap: 12px;
  margin: 12px 0 18px;
}

.review-actions button:disabled {
  opacity: 0.48;
  cursor: not-allowed;
}

.detail-section {
  padding: 16px;
  margin-bottom: 18px;
}

.section-title {
  margin-bottom: 14px;
}

.log-list {
  display: grid;
  gap: 10px;
}

.log-item {
  display: grid;
  grid-template-columns: 14px minmax(0, 1fr);
  gap: 10px;
  padding: 12px;
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.log-item i {
  width: 10px;
  height: 10px;
  margin-top: 5px;
  border-radius: 50%;
  background: #8ff1c7;
}

.log-item.DANGER i {
  background: #ff6b6b;
}

.log-item.WARNING i {
  background: #ffd08a;
}

.log-item span {
  display: block;
  margin-bottom: 5px;
  color: rgba(246, 251, 255, 0.5);
  font-size: 12px;
}

.log-item strong {
  color: #fff;
}

.log-item p,
.detail-note {
  margin: 7px 0 0;
  line-height: 1.65;
}

@media (max-width: 1080px) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .admin-manage-page,
  .review-detail {
    padding: 18px;
  }

  .page-header,
  .drawer-head,
  .review-actions {
    flex-direction: column;
  }

  .filter-card,
  .metric-grid,
  .detail-grid,
  .settlement-grid,
  .closure-panel,
  .closure-grid,
  .proof-grid {
    grid-template-columns: 1fr;
  }
}
</style>
